import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardTitle from "@saleor/components/CardTitle";
import {
  IVoucherLimitActions,
  IVoucherLimitState
} from "@saleor/discounts/hooks/useVoucherLimitsData";
import { DiscountErrorFragment } from "@saleor/fragments/types/DiscountErrorFragment";
import { FormChange } from "@saleor/hooks/useForm";
import React, { Dispatch, FC, useCallback, useEffect } from "react";
import { useIntl } from "react-intl";

import { VoucherDetailsPageFormData } from "../VoucherDetailsPage";
import { useVoucherLimitsStyles } from "./styles";
import LimitCustomerActivityRButton from "./subcomponents/LimitCustomerActivityRButton/LimitCustomerActivityRButton";
import LimitNumOfTimesRButton from "./subcomponents/LimitNumOfTimesRButton/LimitNumOfTimesRButon";
import RadioButton from "./subcomponents/RadioButton/RadioButton";

interface VoucherLimitsProps {
  data: VoucherDetailsPageFormData;
  defaultCurrency: string;
  disabled: boolean;
  errors: DiscountErrorFragment[];
  clientErrors?: {
    usageLimit?: string;
    hasVoucherConditions?: string;
  };
  onChange: FormChange;
  voucherLimitState: IVoucherLimitState;
  voucherLimitDispatcher: Dispatch<IVoucherLimitActions>;
}

const VoucherLimits: FC<VoucherLimitsProps> = ({
  data,
  disabled,
  errors,
  clientErrors,
  onChange,
  voucherLimitState,
  voucherLimitDispatcher
}) => {
  const intl = useIntl();
  const classes = useVoucherLimitsStyles(undefined);

  useEffect(() => {
    if (data.isSpecificCustomer) {
      onChange({ target: { name: "applyToNewCustomer", value: false } });
    }
  }, [data.isSpecificCustomer]);

  const handleRadioButtonToggle = useCallback(
    (formKey: string) => (value: boolean) => {
      if (formKey === "hasVoucherConditions") {
        // hasVoucherConditions is not part of VoucherCreate mutation,
        //   will just store this on voucherLimitState instead.
        if (!value) {
          voucherLimitDispatcher({
            type: "re-initialize",
            voucherLimitState: {
              hasBeenModified: voucherLimitState.hasBeenModified
            }
          });
        } else {
          voucherLimitDispatcher({
            type: "toggle-limit-customer-activity",
            value: true
          });
        }
      } else {
        onChange({
          target: {
            name: formKey,
            value
          }
        });
      }
    },
    [onChange]
  );

  const handleUsageLimitValueChange = useCallback(
    (value: string) => {
      onChange({
        target: {
          name: "usageLimit",
          value
        }
      });
    },
    [onChange]
  );

  return (
    <Card>
      <CardTitle
        title={intl.formatMessage({
          defaultMessage: "Usage Limit",
          description: "voucher usage limit, header"
        })}
      />
      <CardContent className={classes.styledCardContent}>
        <LimitNumOfTimesRButton
          radioButtonValue={data.hasUsageLimit}
          onRadioButtonValueChange={handleRadioButtonToggle("hasUsageLimit")}
          errors={errors}
          isDisabled={disabled}
          usageLimitValue={data.usageLimit}
          clientErrors={clientErrors}
          onUsageLimitValueChange={handleUsageLimitValueChange}
        />
        <div>
          <RadioButton
            label={intl.formatMessage({
              defaultMessage: "Limit to one use per customer"
            })}
            value={data.applyOncePerCustomer}
            onChange={handleRadioButtonToggle("applyOncePerCustomer")}
            isDisabled={disabled}
          />
        </div>
        <LimitCustomerActivityRButton
          radioButtonValue={voucherLimitState.isLimitToCustomerActivity}
          onRadioButtonValueChange={handleRadioButtonToggle(
            "hasVoucherConditions"
          )}
          isDisabled={disabled || data.bulkCreate}
          voucherLimitState={voucherLimitState}
          voucherLimitDispatcher={voucherLimitDispatcher}
          clientErrors={clientErrors}
        />
      </CardContent>
    </Card>
  );
};

export default VoucherLimits;
